// src/Pages/Clients/Sesiones/DrivePanel.js
// Panel de Google Drive para la vista del cliente.
//
// El estudio sube las fotos a Supabase; el cliente las baja a SU Drive. El
// panel no sube archivos: arma las ordenes y las Edge Functions hacen el
// trabajo pesado con el service role.
import React, { useCallback, useEffect, useState } from "react";
import {
  HardDrive,
  CloudUpload,
  Check,
  TriangleAlert,
  RefreshCw,
  Unplug,
  ExternalLink,
  Loader2,
  ShieldCheck,
  Folder,
} from "lucide-react";
import {
  estadoDrive,
  conectarDrive,
  desconectarDrive,
  subirTanda,
  linkCarpetaDrive,
} from "../../../utils/driveService";
import DriveFolderPicker from "./DriveFolderPicker";

const LOTE = 5;
const MAX_TANDAS_VACIAS = 2;

const DrivePanel = ({ sesion, totalFotos, alTerminar }) => {
  const [conexion, setConexion] = useState({ conectado: false });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [carpeta, setCarpeta] = useState(null);
  const [pickerAbierto, setPickerAbierto] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [subiendo, setSubiendo] = useState(false);

  const yaSubidas = Boolean(sesion.subido_google_drive);

  // --- Estado de la conexion ------------------------------------------------
  const refrescar = useCallback(async () => {
    try {
      const datos = await estadoDrive();
      setConexion(datos);
      if (datos.carpetaId) {
        setCarpeta((prev) => prev ?? { id: datos.carpetaId, name: datos.carpetaNombre });
      }
      return datos;
    } catch (err) {
      setError(err.message || String(err));
      return { conectado: false };
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    refrescar();
  }, [refrescar]);

  // --- Vuelve de la pantalla de permisos de Google --------------------------
  // drive-auth-callback redirige aca con ?drive=conectado|error|cancelado.
  // Se lee una vez y se limpia la URL para que un F5 no re-muestre el aviso.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const estado = params.get("drive");
    if (!estado) return;

    const mensaje = params.get("mensaje") || params.get("correo");
    if (estado === "conectado") {
      setAviso({
        tipo: "ok",
        texto: mensaje
          ? `Google Drive conectado como ${mensaje}.`
          : "Google Drive conectado.",
      });
      refrescar();
    } else if (estado === "cancelado") {
      setAviso({ tipo: "info", texto: "Cancelaste la conexión con Google Drive." });
    } else {
      setAviso({
        tipo: "error",
        texto: mensaje || "No se pudo conectar Google Drive.",
      });
    }

    params.delete("drive");
    params.delete("mensaje");
    params.delete("correo");
    const limpio = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
    window.history.replaceState({}, "", limpio);
  }, [refrescar]);

  // --- Conectar / desconectar ----------------------------------------------
  const conectar = async () => {
    setError(null);
    try {
      const url = await conectarDrive();
      // Redireccion completa, no popup: Google no manda postMessage al
      // opener con los permisos y ademas los navegadores los bloquean.
      window.location.assign(url);
    } catch (err) {
      setError(err.message || String(err));
      setCargando(false);
    }
  };

  const desconectar = async () => {
    setError(null);
    try {
      await desconectarDrive();
      setConexion({ conectado: false });
      setCarpeta(null);
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  // --- Subir ----------------------------------------------------------------
  const subir = async () => {
    if (!carpeta?.id) {
      setError("Elegí primero una carpeta de destino.");
      return;
    }

    setSubiendo(true);
    setError(null);
    setProgreso({ subidas: 0, total: totalFotos || 0, errores: [] });

    try {
      let vacias = 0;

      for (let tanda = 0; tanda < 500; tanda++) {
        const r = await subirTanda({
          sesionId: sesion.id,
          folderId: carpeta.id,
          folderName: carpeta.name,
          lote: LOTE,
        });

        setProgreso({
          subidas: r.subidas ?? 0,
          total: r.total ?? totalFotos ?? 0,
          errores: r.errores ?? [],
        });

        if (r.terminado) {
          alTerminar?.(r);
          break;
        }

        // Una tanda que no sube nada y no falla significa que la funcion no
        // esta avanzando. Cortar aca evita quedarse pegado en un bucle.
        vacias = (r.enEstaTanda ?? 0) === 0 ? vacias + 1 : 0;
        if (vacias >= MAX_TANDAS_VACIAS) {
          setError("La subida no avanza. Revisá tu conexión e intentá de nuevo.");
          break;
        }
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubiendo(false);
    }
  };

  const linkDrive = linkCarpetaDrive(sesion.drive_folder_id);
  const porcentaje = progreso?.total
    ? Math.round((progreso.subidas / progreso.total) * 100)
    : 0;

  // --- Vista: ya esta en Drive ---------------------------------------------
  if (yaSubidas) {
    return (
      <section className="sesiones-drive-panel sesiones-drive-panel--listo">
        <div className="sesiones-drive-head">
          <HardDrive size={22} className="sesiones-drive-icon" />
          <div>
            <h3 className="sesiones-drive-title">Tus fotos ya están en tu Drive</h3>
            <p className="sesiones-drive-sub">
              {sesion.drive_fotos_total || totalFotos} fotos en calidad original,
              guardadas en{" "}
              {sesion.drive_folder_nombre || "una carpeta con el nombre de la sesión"}.
            </p>
          </div>
        </div>

        {linkDrive && (
          <a
            className="sesiones-drive-btn sesiones-drive-btn--ghost"
            href={linkDrive}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} /> Abrir en Google Drive
          </a>
        )}

        {aviso && (
          <p className={`sesiones-drive-aviso sesiones-drive-aviso--${aviso.tipo}`}>
            {aviso.texto}
          </p>
        )}
      </section>
    );
  }

  // --- Vista: cargando ------------------------------------------------------
  if (cargando) {
    return (
      <section className="sesiones-drive-panel">
        <p className="sesiones-drive-status">
          <Loader2 size={16} className="sesiones-spin" /> Verificando tu Google Drive...
        </p>
      </section>
    );
  }

  // --- Vista: sin conectar --------------------------------------------------
  if (!conexion.conectado) {
    return (
      <section className="sesiones-drive-panel">
        <div className="sesiones-drive-head">
          <HardDrive size={22} className="sesiones-drive-icon" />
          <div>
            <h3 className="sesiones-drive-title">Llevate tus fotos a tu Google Drive</h3>
            <p className="sesiones-drive-sub">
              Copiamos las fotos de tu sesión a tu propio Drive, en la carpeta
              que elijas y en calidad original. Después las borramos de nuestro
              servidor.
            </p>
          </div>
        </div>

        {aviso && (
          <p className={`sesiones-drive-aviso sesiones-drive-aviso--${aviso.tipo}`}>
            {aviso.texto}
          </p>
        )}

        <p className="sesiones-drive-nota">
          <ShieldCheck size={15} /> Solo vos y Google ven tus archivos. Nosotros
          no guardamos acceso a tu cuenta: es la app, no una persona.
        </p>

        <button type="button" className="sesiones-drive-btn" onClick={conectar}>
          <HardDrive size={16} /> Conectar Google Drive
        </button>

        {error && (
          <p className="sesiones-drive-aviso sesiones-drive-aviso--error">
            <TriangleAlert size={15} /> {error}
          </p>
        )}
      </section>
    );
  }

  // --- Vista: conectado -----------------------------------------------------
  return (
    <section className="sesiones-drive-panel">
      <div className="sesiones-drive-head">
        <HardDrive size={22} className="sesiones-drive-icon" />
        <div>
          <h3 className="sesiones-drive-title">Subir a tu Google Drive</h3>
          <p className="sesiones-drive-sub">
            Conectado como <strong>{conexion.correo || "tu cuenta de Google"}</strong>
            {carpeta ? ` · destino: ${carpeta.name}` : " · todavía no elegiste carpeta"}
          </p>
        </div>
      </div>

      <div className="sesiones-drive-acciones">
        <button
          type="button"
          className="sesiones-drive-btn sesiones-drive-btn--ghost"
          onClick={() => setPickerAbierto(true)}
          disabled={subiendo}
        >
          <Folder size={16} /> {carpeta ? "Cambiar carpeta" : "Elegir carpeta"}
        </button>

        <button
          type="button"
          className="sesiones-drive-btn"
          onClick={subir}
          disabled={subiendo || !carpeta?.id}
        >
          {subiendo ? (
            <>
              <Loader2 size={16} className="sesiones-spin" /> Subiendo...
            </>
          ) : (
            <>
              <CloudUpload size={16} /> Subir {totalFotos || "mis"} fotos
            </>
          )}
        </button>
      </div>

      {subiendo && progreso && (
        <div className="sesiones-drive-progreso">
          <div
            className="sesiones-drive-barra"
            role="progressbar"
            aria-valuenow={progreso.subidas}
            aria-valuemin={0}
            aria-valuemax={progreso.total}
          >
            <span style={{ width: `${porcentaje}%` }} />
          </div>
          <p className="sesiones-drive-progreso-txt">
            {progreso.subidas} de {progreso.total} fotos
            {progreso.errores?.length > 0 &&
              ` · ${progreso.errores.length} con problemas`}
          </p>
        </div>
      )}

      {aviso && (
        <p className={`sesiones-drive-aviso sesiones-drive-aviso--${aviso.tipo}`}>
          {aviso.texto}
        </p>
      )}

      {error && (
        <p className="sesiones-drive-aviso sesiones-drive-aviso--error">
          <TriangleAlert size={15} /> {error}
          <button
            type="button"
            className="sesiones-drive-reintentar"
            onClick={refrescar}
            aria-label="Reintentar"
          >
            <RefreshCw size={14} />
          </button>
        </p>
      )}

      <p className="sesiones-drive-nota">
        <Check size={15} /> Vas a tener 30 días para bajarlas también desde acá.
        Después solo van a estar en tu Drive.
      </p>

      <button
        type="button"
        className="sesiones-drive-desconectar"
        onClick={desconectar}
        disabled={subiendo}
      >
        <Unplug size={14} /> Desconectar mi Drive
      </button>

      <DriveFolderPicker
        abierto={pickerAbierto}
        onClose={() => setPickerAbierto(false)}
        onElegir={(c) => {
          setCarpeta(c);
          setPickerAbierto(false);
          setError(null);
        }}
      />
    </section>
  );
};

export default DrivePanel;